import { c as _c } from "react/compiler-runtime";
import { pinToPositionStyle } from "@osmix/ui-primitives";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { FeedCollection, FeedImage } from "@/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { productHref, rememberIdeaOrigin } from "@/lib/idea-origin";
import { cn } from "@/lib/utils";
import { useFormatPrice } from "@/lib/use-format-price";

// A collection's cover image with its type badge and shoppable hotspots —
// pins positioned over the image that open a product preview on tap (and click
// on desktop), with a link through to the product. Tap behaviour is identical
// on touch and pointer devices, so there's no hover-only interaction. Shared by
// the feed wall (small) and the collection detail hero (large); `imgClassName`
// tunes the image radius/sizing per context.
export function CollectionCover(t0) {
  const $ = _c(25);
  const {
    collection: c,
    image,
    hideBadge,
    hideHotspots,
    imgClassName,
    onImgLoad
  } = t0;
  const {
    t
  } = useTranslation();
  const fmt = useFormatPrice();
  const src = image?.url ?? c.cover;
  let t1;
  if ($[0] !== c.hotspots || $[1] !== c.id || $[2] !== c.slug || $[3] !== c.title || $[4] !== c.type || $[5] !== fmt || $[6] !== hideBadge || $[7] !== hideHotspots || $[8] !== image?.hotspots || $[9] !== imgClassName || $[10] !== onImgLoad || $[11] !== src || $[12] !== t) {
    const hotspots = hideHotspots ? [] : image?.hotspots ?? c.hotspots;
    const origin = {
      collectionId: c.id,
      collectionSlug: c.slug
    };
    const t2 = c.title;
    let t3;
    if ($[14] !== imgClassName) {
      t3 = cn("w-full bg-osmix-gray-100 object-cover", imgClassName);
      $[14] = imgClassName;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    let t4;
    if ($[16] !== c.title || $[17] !== onImgLoad || $[18] !== src || $[19] !== t3) {
      t4 = <img src={src} alt={t2} loading="lazy" onLoad={onImgLoad} className={t3} />;
      $[16] = c.title;
      $[17] = onImgLoad;
      $[18] = src;
      $[19] = t3;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    let t5;
    if ($[21] !== c.type || $[22] !== hideBadge || $[23] !== t) {
      t5 = !hideBadge && <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 type-small font-bold text-osmix-text-1 shadow-sm backdrop-blur">{t(`feed.types.${c.type}`)}</span>;
      $[21] = c.type;
      $[22] = hideBadge;
      $[23] = t;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    t1 = <div className="relative">{t4}{t5}{hotspots.map(h => <Popover key={`${h.productId}-${h.x}-${h.y}`}><PopoverTrigger aria-label={h.productName} className="group/pin absolute z-10 flex size-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full outline-none" style={pinToPositionStyle(h)}><span className="size-5 rounded-full border-2 border-white bg-osmix-tangelo shadow-md ring-1 ring-black/10 transition-transform duration-150 group-hover/pin:scale-110 group-focus-visible/pin:scale-110 group-data-[state=open]/pin:scale-110" /></PopoverTrigger><PopoverContent side="top" align="center" sideOffset={10} className="w-56 rounded-lg border-osmix-gray-300 p-0 shadow-top-deep"><Link to={productHref(h.productId, origin)} onClick={() => rememberIdeaOrigin(h.productId, origin)} className="flex items-center gap-3 rounded-lg p-2 outline-none transition-colors hover:bg-osmix-gray-100 focus-visible:bg-osmix-gray-100"><img src={h.photo} alt="" className="size-14 shrink-0 rounded-md bg-osmix-gray-100 object-cover" /><span className="flex min-w-0 flex-col gap-0.5"><span className="truncate type-small text-osmix-icon">{h.brand}</span><span className="line-clamp-2 type-body2-bold leading-tight text-osmix-text-1">{h.productName}</span><span className="flex items-center gap-1 type-body2-bold text-osmix-tangelo">{fmt(h.price)}<ArrowRight className="size-3.5" /></span></span></Link></PopoverContent></Popover>)}</div>;
    $[0] = c.hotspots;
    $[1] = c.id;
    $[2] = c.slug;
    $[3] = c.title;
    $[4] = c.type;
    $[5] = fmt;
    $[6] = hideBadge;
    $[7] = hideHotspots;
    $[8] = image?.hotspots;
    $[9] = imgClassName;
    $[10] = onImgLoad;
    $[11] = src;
    $[12] = t;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  return t1;
}